'use client';
import { Button, type FileRejection } from '@curology/ui-components-web';
import { ImageUpload } from '@curology/ui-components-web/react/FileUpload';
import CameraIcon from '@curology/ui-components-web/react/Icon/icons/CameraIcon';
import FaceIcon from '@curology/ui-components-web/react/Icon/icons/FaceIcon';
import FrontFacingSelfieIcon from '@curology/ui-components-web/react/Icon/icons/FrontFacingSelfieIcon';
import LeftFacingSelfieIcon from '@curology/ui-components-web/react/Icon/icons/LeftFacingSelfieIcon';
import NeckIcon from '@curology/ui-components-web/react/Icon/icons/NeckIcon';
import RightFacingSelfieIcon from '@curology/ui-components-web/react/Icon/icons/RightFacingSelfieIcon';
import TrashIcon from '@curology/ui-components-web/react/Icon/icons/TrashIcon';
import { Typography } from '@curology/ui-components-web/react/Typography';
import React, { useEffect, useMemo, useState } from 'react';

import { useFlashMessages } from '@/atoms/flashMessages/useFlashMessages';
import { useVisitContext } from '@/services/visitsService/hooks/useVisitContext';
import { acceptMediaAndroid } from '@/utils/images/acceptMediaAndroid';

import { cleanInvalidFileTypeMessage } from '../../survey/surveyjsSurvey/surveyjs/components/photoUpload/utils/helpers';
import { usePhotoUpload } from '../hooks/usePhotoUpload';
import { type PhotoUploader as PhotoUploaderType } from '../types/types';

const PhotoUploader = ({ uploader }: { uploader: PhotoUploaderType }) => {
  const flashMessages = useFlashMessages();
  const { storePhoto, deletePhoto, processing } = usePhotoUpload(
    uploader.perspective,
  );
  const { visit } = useVisitContext();
  const uploadedPhotos = useMemo(() => {
    if (!visit?.photos) {
      return [];
    }
    return visit.photos.filter(
      (photo) => photo.metadata.photo_perspective === uploader.perspective,
    );
  }, [visit, uploader.perspective]);

  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    if (error) {
      flashMessages.error(cleanInvalidFileTypeMessage(error));
    }
  }, [error]);

  const photoUploadIcon = useMemo(() => {
    switch (uploader.icon) {
      case 'camera':
        return <CameraIcon />;
      case 'frontFacingSelfie':
        return <FrontFacingSelfieIcon />;
      case 'leftFacingSelfie':
        return <LeftFacingSelfieIcon />;
      case 'neck':
        return <NeckIcon />;
      case 'rightFacingSelfie':
        return <RightFacingSelfieIcon />;
      default:
        return <FaceIcon />;
    }
  }, [uploader.icon]);

  /**
   * Override the file input's `accept` parameter for Android so we offer the user
   * the choice of file picker or camera to the user. Should use the Dropzone default for
   * all platforms expect Android, which will use the non-standard: `accept="image/*;capture=camera"`
   * See this gist for more details: https://gist.github.com/danawoodman/4788404bc620d5392d111dba98c73873
   */
  const acceptAttribute = acceptMediaAndroid(navigator);

  return (
    <div className="cur-flex-1 cur-text-center">
      <div className="cur-pl-2">
        <Typography className="cur-mb-2" weight="bold">
          Your Photo
        </Typography>
        <div className="cur-flex cur-flex-col">
          <div>
            <ImageUpload
              className="cur-box-border cur-h-full"
              complete={uploadedPhotos.length > 0}
              {...acceptAttribute}
              src={uploadedPhotos[0]?.localUrl}
              data-testid="photo-uploader-new-image-to-upload"
              error={error}
              icon={photoUploadIcon}
              iconSize="md"
              initialLabel={uploader.instructions}
              onFileAdded={async (files: File[]) => {
                await storePhoto({
                  files,
                  onSuccess: () => {
                    setError(undefined);
                  },
                  onError: (message: string) => {
                    setError(new Error(message));
                  },
                });
              }}
              onFileRejected={(fileRejections: FileRejection[]) => {
                setError(new Error(fileRejections[0].errors[0].message));
              }}
              uploading={processing}
              completeAction={
                <Button
                  buttonType="text"
                  icon={<TrashIcon />}
                  onClick={() =>
                    deletePhoto({
                      id: uploadedPhotos[0].id,
                      onError: (message) => setError(new Error(message)),
                    })
                  }
                  size="sm"
                  title="Delete?"
                />
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoUploader;
